import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useAdminLocale } from '../../lib/admin-locale.js';
import { Icon } from './Icon.js';
import { cx } from './utils.js';

export type SelectOption = {
  value: string;
  label: string;
  /** Optional rich visual shown before the label (for example an inline SVG icon). */
  icon?: ReactNode;
  disabled?: boolean;
};

export type SelectProps = {
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  className?: string;
  triggerClassName?: string;
  /** Show clear (×) when there is a value. Default false. */
  clearable?: boolean;
  /** Search field when there are many options. Default: auto above 8 options. */
  searchable?: boolean;
  'aria-label'?: string;
  id?: string;
};

type MenuRect = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  placement: 'top' | 'bottom';
};

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  required = false,
  name,
  className,
  triggerClassName,
  clearable = false,
  searchable,
  'aria-label': ariaLabel,
  id,
}: SelectProps): ReactNode {
  const { text } = useAdminLocale();
  const resolvedPlaceholder = placeholder ?? text('Επιλέξτε…', 'Select…');
  const listId = useId();
  const triggerId = id ?? listId;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [rect, setRect] = useState<MenuRect | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value);
  const enableSearch = searchable ?? options.length > 8;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [...options];
    return options.filter((option) => option.label.toLowerCase().includes(q));
  }, [options, query]);

  function measure(): void {
    const el = rootRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const gap = 6;
    const preferred = Math.min(280, Math.max(160, filtered.length * 40 + (enableSearch ? 52 : 8)));
    const spaceBelow = vh - box.bottom - gap - 8;
    const spaceAbove = box.top - gap - 8;
    const placement: 'top' | 'bottom' =
      spaceBelow < 160 && spaceAbove > spaceBelow ? 'top' : 'bottom';
    const maxHeight = Math.max(120, placement === 'bottom' ? spaceBelow : spaceAbove);
    const height = Math.min(preferred, maxHeight);
    setRect({
      left: box.left,
      width: Math.max(box.width, 200),
      maxHeight: height,
      placement,
      top: placement === 'bottom' ? box.bottom + gap : box.top - gap - height,
    });
  }

  function close(): void {
    setOpen(false);
    setQuery('');
  }

  function openMenu(): void {
    if (disabled) return;
    setOpen(true);
    const index = Math.max(
      0,
      filtered.findIndex((option) => option.value === value),
    );
    setHighlight(index < 0 ? 0 : index);
  }

  function pick(next: string): void {
    onChange(next);
    close();
  }

  useEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }
    measure();
    const onReposition = (event?: Event) => {
      if (event?.type === 'scroll' && menuRef.current?.contains(event.target as Node)) return;
      measure();
    };
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, filtered.length, enableSearch]);

  useEffect(() => {
    if (!open) return;
    function onDoc(event: MouseEvent): void {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setHighlight((current) => {
      if (filtered.length === 0) return 0;
      return Math.min(current, filtered.length - 1);
    });
  }, [filtered, open]);

  function onTriggerKey(event: KeyboardEvent<HTMLButtonElement>): void {
    if (disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) openMenu();
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close();
    }
  }

  function onMenuKey(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      rootRef.current?.querySelector('button')?.focus();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((i) => Math.min(filtered.length - 1, i + 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((i) => Math.max(0, i - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[highlight];
      if (option && !option.disabled) pick(option.value);
    }
  }

  const menu =
    open && rect
      ? createPortal(
          <div
            ref={menuRef}
            id={listId}
            role="listbox"
            aria-labelledby={triggerId}
            tabIndex={-1}
            onKeyDown={onMenuKey}
            className="fixed z-[80] overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900 dark:ring-black/40"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              maxHeight: rect.maxHeight,
            }}
          >
            {enableSearch ? (
              <div className="border-b border-slate-100 dark:border-slate-800 p-2">
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={text('Αναζήτηση…', 'Search…')}
                  aria-label={text('Αναζήτηση επιλογών', 'Search options')}
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 px-2.5 py-1.5 text-sm text-slate-900 dark:text-slate-100 outline-none focus:border-blue-500 focus:bg-white dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:focus:bg-slate-900"
                />
              </div>
            ) : null}
            <div className="max-h-52 overflow-y-auto py-1.5">
              {filtered.length === 0 ? (
                <p className="px-3 py-2.5 text-sm text-slate-500 dark:text-slate-400 dark:text-slate-500">
                  {text('Δεν βρέθηκαν επιλογές.', 'No options found.')}
                </p>
              ) : (
                filtered.map((option, index) => {
                  const active = option.value === value;
                  const focused = index === highlight;
                  return (
                    <button
                      key={`${option.value}-${index}`}
                      type="button"
                      role="option"
                      aria-selected={active}
                      disabled={option.disabled}
                      className={cx(
                        'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition',
                        option.disabled && 'cursor-not-allowed opacity-40',
                        active
                          ? 'bg-[color:var(--admin-selection-bg,#2563EB)] font-semibold text-[color:var(--admin-selection-text,#FFFFFF)]'
                          : focused
                            ? 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
                            : 'text-slate-800 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800',
                      )}
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => {
                        if (!option.disabled) pick(option.value);
                      }}
                    >
                      <span className="flex min-w-0 flex-1 items-center gap-2">
                        {option.icon ? (
                          <span
                            className="grid size-5 shrink-0 place-items-center"
                            aria-hidden="true"
                          >
                            {option.icon}
                          </span>
                        ) : null}
                        <span className="min-w-0 truncate">{option.label}</span>
                      </span>
                      {active ? (
                        <Icon
                          name="check"
                          className="size-4 shrink-0 text-[color:var(--admin-selection-text,#FFFFFF)]"
                        />
                      ) : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div
      ref={rootRef}
      className={cx(
        'relative min-w-0',
        !(className && /(?:^|\s)(?:!)?(?:\S*:)?(?:max-|min-)?w-/.test(className)) && 'w-full',
        className,
      )}
      data-ui="select"
    >
      {name ? <input type="hidden" name={name} value={value} required={required} /> : null}
      <button
        id={triggerId}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        aria-required={required || undefined}
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKey}
        className={cx(
          'flex h-10 w-full items-center gap-2 rounded-lg border bg-white dark:bg-slate-900 px-3 text-left text-sm shadow-sm transition',
          'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-950 dark:hover:border-slate-500',
          'focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none',
          'disabled:cursor-not-allowed disabled:bg-slate-50 dark:bg-slate-950 disabled:opacity-60 dark:disabled:bg-slate-900',
          open && 'border-blue-500 ring-2 ring-blue-500',
          triggerClassName,
        )}
      >
        {selected?.icon ? (
          <span
            className="grid size-5 shrink-0 place-items-center text-slate-500 dark:text-slate-400"
            aria-hidden="true"
          >
            {selected.icon}
          </span>
        ) : null}
        <span
          className={cx(
            'min-w-0 flex-1 truncate',
            selected
              ? 'font-medium text-slate-900 dark:text-slate-100'
              : 'text-slate-400 dark:text-slate-500 dark:text-slate-400 dark:text-slate-500',
          )}
        >
          {selected?.label ?? resolvedPlaceholder}
        </span>
        {clearable && value && !disabled ? (
          <span
            role="button"
            tabIndex={-1}
            aria-label={text('Καθαρισμός', 'Clear')}
            className="grid size-6 place-items-center rounded-md text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            onClick={(event) => {
              event.stopPropagation();
              onChange('');
            }}
          >
            <Icon name="x" className="size-3.5" />
          </span>
        ) : null}
        <span
          className={cx(
            'grid size-6 shrink-0 place-items-center rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 dark:text-slate-500 transition dark:bg-slate-800 dark:text-slate-400 dark:text-slate-500',
            open &&
              'bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300',
          )}
        >
          <Icon name="chevron" className={cx('size-3.5', open && 'rotate-180')} />
        </span>
      </button>
      {menu}
    </div>
  );
}
